/**
 * Batch 9 · iter_162 — AI writing assistant panel for the ナレッジ editor.
 *
 * Consumed only by ArticleEditor when `ai_prefs.enabled === true`.
 * Streams tokens from POST /api/ai-knowledge/stream (SSE) into a
 * scratchpad the user can insert into the editor, append, copy, or
 * discard. Nothing is auto-inserted — user is always in control.
 *
 * Design notes:
 *  * We use `fetch()` with a manual ReadableStream reader instead of
 *    EventSource because the browser's EventSource cannot send POST
 *    bodies (and we need the request body for action + prompt).
 *  * Cancellation is cooperative — `AbortController.abort()` closes the
 *    stream; the backend's `is_disconnected()` polling picks it up.
 *  * The panel is stateful (last output survives collapsing / re-opening)
 *    so the user can compare drafts without losing the previous one.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkle, X, ClipboardText, Trash, PaperPlaneTilt, Stop, ArrowElbowRightDown,
  PencilSimple, ListBullets, TextT, Translate, Globe,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIONS = [
  { id: 'compose',   label: '作成',    Icon: PencilSimple, hint: '指示を入力し、新しい文章を生成します。',    needs: 'prompt' },
  { id: 'rewrite',   label: '書き直し', Icon: TextT,        hint: '選択したテキストを、より明快な表現に書き直します。', needs: 'selection' },
  { id: 'summarize', label: '要約',    Icon: ListBullets,   hint: '記事全体を短くまとめます。',              needs: 'article' },
  { id: 'translate', label: '翻訳',    Icon: Translate,     hint: '選択したテキストまたは記事全体を日本語↔英語で翻訳します。', needs: 'selection-or-article' },
];

export function AiAssistantPanel({
  open,
  onClose,
  articleId,
  selectionText,
  onInsert,
  onAppend,
  onReplace,
  enabled,
}) {
  const [action, setAction] = useState('compose');
  const [prompt, setPrompt] = useState('');
  const [output, setOutput] = useState('');
  const [citations, setCitations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [useWeb, setUseWeb] = useState(false);
  const [webAvailable, setWebAvailable] = useState(false);
  const abortRef = useRef(null);
  const outputBoxRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/ai/prefs`, { withCredentials: true })
      .then((r) => setWebAvailable(!!r.data?.web_search_available)).catch(() => {});
  }, []);

  // Auto-scroll the scratchpad to the bottom while tokens stream in.
  useEffect(() => {
    if (outputBoxRef.current) {
      outputBoxRef.current.scrollTop = outputBoxRef.current.scrollHeight;
    }
  }, [output]);

  const canRun = useMemo(() => {
    if (busy) return false;
    const cfg = ACTIONS.find((a) => a.id === action);
    if (!cfg) return false;
    if (cfg.needs === 'prompt') return !!prompt.trim();
    if (cfg.needs === 'selection') return !!(selectionText && selectionText.trim());
    if (cfg.needs === 'article') return !!articleId;
    if (cfg.needs === 'selection-or-article') return !!((selectionText && selectionText.trim()) || articleId);
    return false;
  }, [action, prompt, selectionText, articleId, busy]);

  const runStream = async () => {
    if (!canRun) return;
    setOutput('');
    setCitations([]);
    setBusy(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      // Build the request body per action. Selection text is included
      // when relevant so the backend can wrap it as untrusted context.
      const body = {
        action,
        user_prompt: prompt || '',
        selection_text: (action === 'rewrite' || action === 'translate') ? (selectionText || null) : null,
        article_id: (action === 'summarize' || action === 'translate') ? (articleId || null) : null,
        use_web: !!(useWeb && webAvailable),
      };
      const resp = await fetch(`${API}/ai-knowledge/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: ctrl.signal,
      });
      if (!resp.ok) {
        const errBody = await resp.text().catch(() => '');
        let msg = 'AIの呼び出しに失敗しました';
        try { msg = JSON.parse(errBody).detail || msg; } catch { /* ignore */ }
        throw new Error(msg);
      }
      const reader = resp.body.getReader();
      const dec = new TextDecoder('utf-8');
      let buf = '';
      // SSE parser — messages are separated by \n\n. Each frame may
      // contain multiple `data:` lines when the server sent a multi-
      // line chunk (backend collapses newlines to `\n` but future-
      // proofs for other providers).
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf('\n\n')) >= 0) {
          const frame = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          let evt = 'message';
          let data = '';
          for (const raw of frame.split('\n')) {
            if (raw.startsWith('event:')) evt = raw.slice(6).trim();
            else if (raw.startsWith('data:')) { data = raw.slice(5); if (data.startsWith(' ')) data = data.slice(1); }
          }
          if (evt === 'meta') {
            try { setCitations(JSON.parse(data).citations || []); } catch { /* ignore */ }
            continue;
          }
          if (evt === 'error') {
            // Error frames carry a JSON payload — toast it, never append
            // it into the drafted text.
            let msg = 'AIサーバーからエラー応答を受信しました';
            try { msg = JSON.parse(data).error || msg; } catch { /* ignore */ }
            toast.error(msg);
            continue;
          }
          if (data === '[DONE]') { setBusy(false); continue; }
          // Restore \n escape written by backend.
          if (data) setOutput((prev) => prev + data.replace(/\\n/g, '\n'));
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        toast.error(e.message || 'AIの呼び出しに失敗しました');
      }
    } finally {
      abortRef.current = null;
      setBusy(false);
    }
  };

  const stop = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const copyOutput = async () => {
    try {
      await navigator.clipboard.writeText(output);
      toast.success('クリップボードにコピーしました');
    } catch {
      toast.error('コピーに失敗しました');
    }
  };

  if (!open) return null;
  return (
    <aside className="fixed top-14 sm:top-16 bottom-0 right-0 z-40 flex w-[380px] max-w-[95vw] flex-col border-l border-zinc-200 bg-white shadow-xl" data-testid="ai-assistant-panel">
      <header className="flex items-center gap-2 border-b border-zinc-200 px-4 py-3">
        <div className="rounded-lg bg-indigo-50 p-1.5 text-indigo-600">
          <Sparkle size={16} weight="fill" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-zinc-900">AIアシスタント</div>
          <div className="text-[11px] text-zinc-500">下書きを生成し、確認してから本文に挿入できます。</div>
        </div>
        <button type="button" onClick={onClose} data-testid="ai-panel-close"
          className="rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700" title="閉じる">
          <X size={16} />
        </button>
      </header>

      {!enabled ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center" data-testid="ai-panel-disabled">
          <div className="rounded-full bg-zinc-100 p-3 text-zinc-400"><Sparkle size={24} /></div>
          <div className="text-sm font-medium text-zinc-700">AIアシスタントは無効です</div>
          <div className="text-xs text-zinc-500">「設定 → AIアシスタント」から有効化してください。</div>
        </div>
      ) : (
        <>
          <div className="border-b border-zinc-200 px-4 py-3">
            <div className="grid grid-cols-4 gap-1.5">
              {ACTIONS.map((a) => {
                const Icon = a.Icon;
                const isActive = action === a.id;
                return (
                  <button key={a.id} type="button" onClick={() => setAction(a.id)}
                    data-testid={`ai-action-${a.id}`}
                    title={a.hint}
                    className={`flex flex-col items-center justify-center gap-1 rounded-md border px-1.5 py-2 text-[11px] transition-colors ${isActive ? 'border-indigo-400 bg-indigo-50 text-indigo-700' : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50'}`}>
                    <Icon size={16} weight={isActive ? 'fill' : 'regular'} />
                    <span>{a.label}</span>
                  </button>
                );
              })}
            </div>
            <div className="mt-2 text-[11px] leading-relaxed text-zinc-500" data-testid="ai-panel-action-hint">
              {ACTIONS.find((a) => a.id === action)?.hint}
            </div>
          </div>

          <div className="border-b border-zinc-200 px-4 py-3">
            <label className="mb-1.5 block text-[11px] font-medium text-zinc-600">
              {action === 'compose' ? '指示' : '追加指示 (任意)'}
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              data-testid="ai-panel-prompt"
              placeholder={action === 'compose' ? 'たとえば「〇〇工程の作業手順を、初心者向けに書いてください」' : '(任意) スタイルなどの追加指示があれば入力'}
              rows={3}
              className="w-full resize-none rounded-md border border-zinc-200 px-2.5 py-2 text-sm outline-none focus:border-indigo-400" />
            {(action === 'rewrite' || action === 'translate') && !selectionText && (
              <div className="mt-1 text-[11px] text-amber-600" data-testid="ai-panel-need-selection">
                本文内でテキストを選択してから実行してください。
              </div>
            )}
            {action === 'summarize' && !articleId && (
              <div className="mt-1 text-[11px] text-amber-600">
                この記事はまだ保存されていません。先に保存してください。
              </div>
            )}
            {webAvailable && (
              <label className="mt-2 flex cursor-pointer items-center gap-2 text-[11px] text-zinc-600" data-testid="ai-panel-use-web">
                <input type="checkbox" checked={useWeb} onChange={(e) => setUseWeb(e.target.checked)}
                  className="h-3.5 w-3.5 accent-indigo-600" />
                <Globe size={12} className="text-indigo-500" /> Web検索を使う（インターネットの情報を参照）
              </label>
            )}
            <div className="mt-3 flex items-center gap-2">
              {busy ? (
                <Button onClick={stop} variant="destructive" data-testid="ai-panel-stop" className="flex-1">
                  <Stop size={14} weight="fill" className="mr-1.5" />停止
                </Button>
              ) : (
                <Button onClick={runStream} disabled={!canRun} data-testid="ai-panel-run" className="flex-1 bg-teal-700 hover:bg-teal-800">
                  <PaperPlaneTilt size={14} weight="fill" className="mr-1.5" />実行
                </Button>
              )}
            </div>
          </div>

          <div ref={outputBoxRef} className="flex-1 overflow-y-auto px-4 py-3" data-testid="ai-panel-output">
            {output ? (
              <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-zinc-800">{output}</pre>
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-xs text-zinc-400">
                <Sparkle size={20} />
                <div>実行するとここに結果が表示されます</div>
              </div>
            )}
            {busy && <div className="mt-3 text-[11px] italic text-zinc-500" data-testid="ai-panel-streaming">生成中…</div>}
            {!busy && output && citations.length > 0 && (
              <div className="mt-3 border-t border-zinc-200 pt-2" data-testid="ai-panel-citations">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">参照元</p>
                <div className="space-y-1">
                  {citations.slice(0, 8).map((c, i) => (
                    <a key={i} href={c.source_type === 'doc' ? `/documents?open=${c.article_id}` : `/knowledge/p/${c.article_id}`}
                      target="_blank" rel="noreferrer"
                      className="group flex items-center gap-1.5 text-[11px] text-zinc-600 hover:text-indigo-700"
                      data-testid={`ai-panel-citation-${i}`}>
                      <span className={`shrink-0 rounded border px-1 py-px text-[9px] font-bold ${c.source_type === 'doc'
                        ? 'border-violet-100 bg-violet-50 text-violet-600'
                        : 'border-indigo-100 bg-indigo-50 text-indigo-600'}`}>
                        {c.source_type === 'doc' ? '書類' : 'ナレッジ'}
                      </span>
                      <span className="truncate group-hover:underline">{c.title}{c.doc_number ? ` ${c.doc_number}` : ''}（関連度 {c.score}）</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <footer className="grid grid-cols-4 gap-1.5 border-t border-zinc-200 px-3 py-3">
            <button type="button" onClick={() => onInsert?.(output)}
              disabled={!output || busy}
              data-testid="ai-panel-insert"
              className="flex flex-col items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-2 text-[11px] text-emerald-700 transition-colors hover:bg-emerald-100 disabled:opacity-40 disabled:hover:bg-emerald-50">
              <ArrowElbowRightDown size={14} weight="fill" />本文に挿入
            </button>
            <button type="button" onClick={() => onAppend?.(output)}
              disabled={!output || busy}
              data-testid="ai-panel-append"
              className="flex flex-col items-center gap-1 rounded-md border border-zinc-200 bg-white px-2 py-2 text-[11px] text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-40">
              <PaperPlaneTilt size={14} />末尾に追加
            </button>
            <button type="button" onClick={copyOutput}
              disabled={!output || busy}
              data-testid="ai-panel-copy"
              className="flex flex-col items-center gap-1 rounded-md border border-zinc-200 bg-white px-2 py-2 text-[11px] text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-40">
              <ClipboardText size={14} />コピー
            </button>
            <button type="button" onClick={() => setOutput('')}
              disabled={!output || busy}
              data-testid="ai-panel-clear"
              className="flex flex-col items-center gap-1 rounded-md border border-zinc-200 bg-white px-2 py-2 text-[11px] text-zinc-500 transition-colors hover:bg-zinc-50 hover:text-rose-600 disabled:opacity-40">
              <Trash size={14} />破棄
            </button>
          </footer>
          {action === 'rewrite' && (
            <div className="border-t border-zinc-200 bg-zinc-50/60 px-4 py-2 text-[11px] text-zinc-500">
              「本文に挿入」は選択範囲を置き換えます。
            </div>
          )}
        </>
      )}
    </aside>
  );
}

export default AiAssistantPanel;
